import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  LinearProgress,
  Pagination,
  Grid,
  Avatar,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Devices,
  Add,
  MoreVert,
  Edit,
  Delete,
  Router,
  Wifi,
  Cable,
  CheckCircle,
  Warning,
  Error,
  AssignmentReturn,
  Settings,
  SwapHoriz,
  Phone,
  Tv,
  Person,
  Close as CloseIcon,
} from '@mui/icons-material';
import AddOnuModal from './AddOnuModal';
import TrocarCtoModal from './TrocarCtoModal';
import TrocarEquipamentoModal from './TrocarEquipamentoModal';
import ProvisionarModal from '../modals/ProvisionarModal';
import equipmentService from '../../services/equipmentService';
import { Equipment } from '../../types';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import { useContextMenu } from '../../hooks/useContextMenu';
import { EquipmentContextMenu } from '../common/ContextMenu';
import AddEquipamentoModal from './AddEquipamentoModal';
import EquipmentForm from '../forms/EquipmentForm';
import TransferEquipamentoModal from '../modals/TransferEquipamentoModal';
import VincularCircuitoModal from './VincularCircuitoModal';
import CircuitoViewModal from '../modals/CircuitoViewModal';
import { useNavigate } from 'react-router-dom';

// Interface estendida para equipamentos com dados de CAIXA
interface EquipmentWithCaixa extends Equipment {
  caixa_id?: number;
  caixa_port_number?: number;
  contrato_id?: number;
  caixa_name?: string;
  caixa?: {
    id: number;
    name: string;
    olt_id?: number;
  };
}

interface ContractEquipmentsProps {
  contractId: number;
  contractStatus: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura' | 'suspenso';
}

const ContractEquipments: React.FC<ContractEquipmentsProps> = ({ contractId, contractStatus }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [equipments, setEquipments] = useState<EquipmentWithCaixa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Menu de contexto
  const {
    anchorEl,
    contextData: selectedEquipment,
    mouseX,
    mouseY,
    handleContextMenu,
    handleClose: handleMenuClose,
    isOpen: isMenuOpen
  } = useContextMenu();

  const [addOnuModalOpen, setAddOnuModalOpen] = useState(false);
  const [provisionarModalOpen, setProvisionarModalOpen] = useState(false);
  const [selectedEquipmentForProvision, setSelectedEquipmentForProvision] = useState<EquipmentWithCaixa | null>(null);
  const [addEquipamentoModalOpen, setAddEquipamentoModalOpen] = useState(false);
  const [editEquipmentModalOpen, setEditEquipmentModalOpen] = useState(false);
  const [selectedEquipmentForEdit, setSelectedEquipmentForEdit] = useState<EquipmentWithCaixa | null>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferringEquipment, setTransferringEquipment] = useState<EquipmentWithCaixa | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [vincularCircuitoModalOpen, setVincularCircuitoModalOpen] = useState(false);
  const [selectedEquipmentForCircuito, setSelectedEquipmentForCircuito] = useState<EquipmentWithCaixa | null>(null);
  const [trocarCtoModalOpen, setTrocarCtoModalOpen] = useState(false);
  const [selectedEquipmentForTrocarCto, setSelectedEquipmentForTrocarCto] = useState<EquipmentWithCaixa | null>(null);
  const [trocarEquipamentoModalOpen, setTrocarEquipamentoModalOpen] = useState(false);
  const [selectedEquipmentForTrocarEquipamento, setSelectedEquipmentForTrocarEquipamento] = useState<EquipmentWithCaixa | null>(null);

  // Estados para modal de visualização de circuito
  const [circuitoViewModalOpen, setCircuitoViewModalOpen] = useState(false);
  const [selectedCircuitoId, setSelectedCircuitoId] = useState<number | null>(null);

  const handleOpenCircuitoView = (circuitoId: number) => {
    setSelectedCircuitoId(circuitoId);
    setCircuitoViewModalOpen(true);
  };

  const handleCloseCircuitoView = () => {
    setCircuitoViewModalOpen(false);
    setSelectedCircuitoId(null);
  };

  // Estados para visualização de imagem
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  // Handlers para o menu de contexto
  const handleProvisionar = async (equipmentId: number) => {
    try {
      const equipment = equipments.find(eq => eq.id === equipmentId);
      if (!equipment) return;

      console.log('🚀 ContractEquipments - Abrindo modal de provisionamento para equipamento:', equipmentId);

      if (!equipment.caixa?.olt_id) {
        await Swal.fire({
          title: 'Atenção',
          text: 'É obrigatório vincular uma OLT a esta caixa antes de realizar o provisionamento.',
          icon: 'warning',
          confirmButtonText: 'OK',
          customClass: {
            container: 'swal-zindex'
          }
        });
        return;
      }

      // Definir equipamento selecionado e abrir modal
      setSelectedEquipmentForProvision(equipment);
      setProvisionarModalOpen(true);
    } catch (error: any) {
      console.error('❌ ContractEquipments - Erro ao abrir modal de provisionamento:', error);

      const errorMessage = error?.message || error?.toString() || 'Erro desconhecido ao abrir modal de provisionamento';

      await Swal.fire({
        title: 'Erro!',
        text: errorMessage,
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    }
  };

  const handleProvisionSuccess = () => {
    // Recarregar lista após provisionamento bem-sucedido
    loadEquipments();
  };

  const handleCloseProvisionarModal = () => {
    setProvisionarModalOpen(false);
    setSelectedEquipmentForProvision(null);
  };

  const handleChecarSinal = async (equipmentId: number) => {
    try {
      const equipment = equipments.find(eq => eq.id === equipmentId);
      if (!equipment) return;

      // Mostrar loading
      Swal.fire({
        title: 'Verificando Sinal...',
        html: `
          <div style="text-align: center;">
            <div class="swal2-spacer" style="margin: 20px 0;"></div>
            <p>Aguarde, estamos verificando o sinal do equipamento...</p>
            <div class="swal2-spacer" style="margin: 10px 0;"></div>
            <p style="font-size: 14px; color: #666;">
              <strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}<br>
              <strong>GPON SN:</strong> ${equipment.gpon_sn || 'N/A'}
            </p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: {
          container: 'swal-zindex'
        },
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await equipmentService.checarSinalEquipamento(equipmentId);
      console.log("Resposta: ", response);

      if (response.success) {
        // Recarregar lista
        loadEquipments();

        const signalData = response.data;
        if (response.debug_result) {
          await Swal.fire({
            title: 'Sinal do Equipamento',
            html: `
              <div style="text-align: left;">
                <p><strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}</p>
                <p><strong>Rx Power:</strong> ${signalData?.onuInfo?.data?.rxPower || 'N/A'} dBm</p>
                <p><strong>Tx Power:</strong> ${signalData?.onuInfo?.data?.txPower || 'N/A'} dBm</p>
                <p><strong>Status:</strong> ${signalData?.onuInfo?.data?.status || 'N/A'}</p>
                <p><strong>Distância:</strong> ${signalData?.onuInfo?.data?.distance} km</p>
                <br>
                <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                <pre style="text-align: left; background: #2b2b2b; color: #72f542; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>
              </div>
            `,
            icon: 'info',
            width: '600px',
            customClass: {
              container: 'swal-zindex'
            }
          });
        } else {
          await Swal.fire({
            title: 'Sinal do Equipamento',
            html: `
              <div style="text-align: left;">
                <p><strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}</p>
                <p><strong>Rx Power:</strong> ${signalData?.onuInfo?.data?.rxPower || 'N/A'} dBm</p>
                <p><strong>Tx Power:</strong> ${signalData?.onuInfo?.data?.txPower || 'N/A'} dBm</p>
                <p><strong>Status:</strong> ${signalData?.onuInfo?.data?.status || 'N/A'}</p>
                <p><strong>Distância:</strong> ${signalData?.onuInfo?.data?.distance || 'N/A'} km</p>
              </div>
              ${response.debug_result ? `
                <br>
                <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                <pre style="text-align: left; background: #2b2b2b; color: #72f542; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>
              ` : ''}
            `,
            icon: 'info',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      } else {
        if (response.debug_result) {
          await Swal.fire({
            title: 'Erro ao Verificar Sinal',
            html: `
              <div style="text-align: left;">
                <p>${response.error || 'Erro ao verificar sinal'}</p>
                <br>
                <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>
              </div>
            `,
            icon: 'error',
            width: '600px',
            customClass: {
              container: 'swal-zindex'
            }
          });
        } else {
          await Swal.fire({
            title: 'Erro!',
            text: response.error || 'Erro ao verificar sinal',
            icon: 'error',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao verificar sinal:', error);
      await Swal.fire({
        title: 'Erro!',
        text: 'Erro ao verificar sinal do equipamento',
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    }
  };

  // Resto das funções existentes...
  const handleDiagnosticarONU = async (equipmentId: number) => {
    try {
      const equipment = equipments.find(eq => eq.id === equipmentId);
      if (!equipment) return;

      // Mostrar loading
      Swal.fire({
        title: 'Diagnosticando ONU...',
        html: `
          <div style="text-align: center;">
            <div class="swal2-spacer" style="margin: 20px 0;"></div>
            <p>Aguarde, estamos verificando todas as informações do Equipamento...</p>
            <div class="swal2-spacer" style="margin: 10px 0;"></div>
            <p style="font-size: 14px; color: #666;">
              <strong>Equipamento:</strong> ${equipment?.modelo?.titulo || 'N/A'}<br>
              <strong>GPON SN:</strong> ${equipment?.gpon_sn || 'N/A'}
            </p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: {
          container: 'swal-zindex'
        },
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await equipmentService.diagnosticarONU(equipmentId);
      console.log("Resposta: ", response);

      if (response.success) {
        // Recarregar lista
        loadEquipments();

        const signalData = response.data;
        if (response.debug_result) {
          await Swal.fire({
            title: 'Diagnóstico do Equipamento',
            html: `
              <div style="text-align: left;">                
                <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                <p><strong>Equipamento:</strong> ${equipment?.modelo?.titulo || 'N/A'}</p>
                <p><strong>Status:</strong> ${signalData?.status || 'N/A'}</p>
                <p><strong>Última causa de queda:</strong> ${signalData?.lastDownCauseSignificado || 'N/A'}</p>
                <p><strong>IP da ONU:</strong> ${signalData?.ontIp || 'N/A'}</p>
                <p><strong>Distância:</strong> ${signalData?.distance || 'N/A'} metros</p>
                <p><strong>Rx Power:</strong> ${signalData?.rxPower || 'N/A'} dBm</p>
                <p><strong>Tx Power:</strong> ${signalData?.txPower || 'N/A'} dBm</p>
                <pre style="text-align: left; background: #2b2b2b; color: #72f542; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>
              </div>
            `,
            width: '800px',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      } else {
        if (response.debug_result) {
          await Swal.fire({
            title: 'Erro de Diagnóstico',
            html: `
              <div style="text-align: left;">
                <p>${response.error || 'Erro ao diagnosticar ONU'}</p>
                <br>
                <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>
              </div>
            `,
            icon: 'error',
            width: '600px',
            customClass: {
              container: 'swal-zindex'
            }
          });
        } else {
          await Swal.fire({
            title: 'Erro!',
            text: response.error || 'Erro ao diagnosticar ONU',
            icon: 'error',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao diagnosticar ONU:', error);
      await Swal.fire({
        title: 'Erro!',
        text: 'Erro ao diagnosticar ONU',
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    }
  };

  const handleDesprovisionar = async (equipmentId: number) => {
    try {
      const equipment = equipments.find(eq => eq.id === equipmentId);
      if (!equipment) return;

      const result = await Swal.fire({
        title: 'Desprovisionar Equipamento',
        html: `
          <div style="text-align: left;">
            <p><strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}</p>
            <p><strong>Série:</strong> ${equipment.numero_serie}</p>
            <p><strong>GPON SN:</strong> ${equipment.gpon_sn || 'N/A'}</p>
            <br>
            <p>Tem certeza que deseja desprovisionar este equipamento da OLT?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, desprovisionar!',
        cancelButtonText: 'Cancelar',
        customClass: {
          container: 'swal-zindex'
        }
      });

      if (result.isConfirmed) {
        console.log('🚀 ContractEquipments - Iniciando desprovisionamento do equipamento:', equipmentId);

        // Mostrar loading
        Swal.fire({
          title: 'Desprovisionando...',
          html: `
            <div style="text-align: center;">
              <div class="swal2-spacer" style="margin: 20px 0;"></div>
              <p>Aguarde, estamos desprovisionando o equipamento da OLT...</p>
              <div class="swal2-spacer" style="margin: 10px 0;"></div>
              <p style="font-size: 14px; color: #666;">
                <strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}<br>
                <strong>GPON SN:</strong> ${equipment.gpon_sn || 'N/A'}
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            container: 'swal-zindex'
          },
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          const response = await equipmentService.desprovisionarEquipamento(equipmentId);

          if (response.success) {
            if (response.debug_result) {
              await Swal.fire({
                title: 'Equipamento Desprovisionado',
                html: `<p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                       <pre style="text-align: left; background: #2b2b2b; color: #72f542; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>`,
                icon: 'success',
                width: '600px',
                customClass: {
                  container: 'swal-zindex'
                }
              });
            } else {
              await Swal.fire({
                title: 'Sucesso!',
                text: response.message,
                icon: 'success',
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                  container: 'swal-zindex'
                }
              });
            }

            // Recarregar lista
            loadEquipments();
          } else {
            if (response.debug_result) {
              await Swal.fire({
                title: 'Erro de Desprovisionamento',
                html: `<p style="text-align: left; margin-bottom: 10px;">${response.error || response.message || 'Falha ao desprovisionar na OLT.'}</p>
                       <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>`,
                icon: 'error',
                width: '600px',
                customClass: {
                  container: 'swal-zindex'
                }
              });
            } else {
              await Swal.fire({
                title: 'Erro no Desprovisionamento!',
                text: response.error || 'Erro ao desprovisionar equipamento',
                icon: 'error',
                customClass: {
                  container: 'swal-zindex'
                }
              });
            }
          }
        } catch (error: any) {
          console.error('❌ ContractEquipments - Erro na requisição:', error);

          const errorMessage = error?.message || error?.toString() || 'Erro desconhecido ao desprovisionar equipamento';

          await Swal.fire({
            title: 'Erro!',
            text: errorMessage,
            icon: 'error',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      }
    } catch (error: any) {
      console.error('❌ Erro ao desprovisionar equipamento:', error);

      const errorMessage = error?.message || error?.toString() || 'Erro desconhecido ao desprovisionar equipamento';

      await Swal.fire({
        title: 'Erro!',
        text: errorMessage,
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    }
  };

  const handleReiniciar = async (equipmentId: number) => {
    try {
      const equipment = equipments.find(eq => eq.id === equipmentId);
      if (!equipment) return;

      const result = await Swal.fire({
        title: 'Reiniciar Equipamento',
        html: `
          <div style="text-align: left;">
            <p><strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}</p>
            <p><strong>Série:</strong> ${equipment.numero_serie}</p>
            <br>
            <p><strong>ATENÇÃO:</strong> O equipamento será reiniciado e ficará offline temporariamente.</p>
            <p>Tem certeza que deseja reiniciar este equipamento?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, reiniciar!',
        cancelButtonText: 'Cancelar',
        customClass: {
          container: 'swal-zindex'
        }
      });

      if (result.isConfirmed) {
        // Mostrar loading
        Swal.fire({
          title: 'Reiniciando...',
          html: `
            <div style="text-align: center;">
              <div class="swal2-spacer" style="margin: 20px 0;"></div>
              <p>Aguarde, estamos reiniciando o equipamento...</p>
              <div class="swal2-spacer" style="margin: 10px 0;"></div>
              <p style="font-size: 14px; color: #666;">
                <strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}<br>
                <strong>Série:</strong> ${equipment.numero_serie}
              </p>
              <div class="swal2-spacer" style="margin: 10px 0;"></div>
              <p style="font-size: 12px; color: #ff6b6b;">
                ⚠️ O equipamento ficará offline temporariamente
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            container: 'swal-zindex'
          },
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          const response = await equipmentService.reiniciarEquipamento(equipmentId);

          if (response.success) {
            await Swal.fire({
              title: 'Sucesso!',
              text: response.message,
              icon: 'success',
              timer: 3000,
              timerProgressBar: true,
              customClass: {
                container: 'swal-zindex'
              }
            });
          } else {
            await Swal.fire({
              title: 'Erro!',
              text: response.error || 'Erro ao reiniciar equipamento',
              icon: 'error',
              customClass: {
                container: 'swal-zindex'
              }
            });
          }
        } catch (error: any) {
          console.error('Erro ao reiniciar equipamento:', error);
          await Swal.fire({
            title: 'Erro!',
            text: 'Erro ao reiniciar equipamento',
            icon: 'error',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao reiniciar equipamento:', error);
      await Swal.fire({
        title: 'Erro!',
        text: 'Erro ao reiniciar equipamento',
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    }
  };

  useEffect(() => {
    loadEquipments();
  }, [contractId, page]);

  const loadEquipments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar apenas equipamentos vinculados ao contrato específico
      const response = await equipmentService.getEquipmentsByContract(contractId);

      if (!response || !response.success) {
        setError('Erro ao buscar equipamentos do contrato');
        return;
      }

      let equipmentsList: EquipmentWithCaixa[] = [];

      // A nova API retorna os equipamentos diretamente em response.data (array)
      if (response.data && Array.isArray(response.data)) {
        equipmentsList = response.data as EquipmentWithCaixa[];
      } else {
        console.error('❌ Estrutura de resposta inesperada:', response);
        setError('Formato de dados inesperado da API de equipamentos');
        return;
      }

      setEquipments(equipmentsList);
      setTotalPages(1); // Como estamos buscando apenas os equipamentos deste contrato

    } catch (err: any) {
      console.error('❌ Erro ao carregar equipamentos:', err);
      setError('Erro ao carregar equipamentos do contrato');
    } finally {
      setLoading(false);
    }
  };



  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'provisioned':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Warning />;
      case 'provisioned':
        return <CheckCircle />;
      case 'failed':
        return <Error />;
      default:
        return <Devices />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pendente';
      case 'provisioned':
        return 'Provisionado';
      case 'failed':
        return 'Falha';
      default:
        return status || 'N/A';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'roteador':
        return <Router />;
      case 'onu':
      case 'ont':
        return <Wifi />;
      case 'switch':
        return <Cable />;
      default:
        return <Devices />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'roteador':
        return 'Roteador';
      case 'onu':
        return 'ONU';
      case 'ont':
        return 'ONT';
      case 'switch':
        return 'Switch';
      default:
        return type || 'N/A';
    }
  };

  const getModalidadeColor = (modalidade: string) => {
    switch (modalidade?.toLowerCase()) {
      case 'proprio':
        return 'primary';
      case 'comodato':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getModalidadeLabel = (modalidade: string) => {
    switch (modalidade?.toLowerCase()) {
      case 'proprio':
        return 'Próprio';
      case 'comodato':
        return 'Comodato';
      default:
        return modalidade || 'N/A';
    }
  };

  const getTipoServicoIcon = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'internet':
        return <Wifi />;
      case 'telefonia':
        return <Phone />;
      case 'tv':
        return <Tv />;
      default:
        return <Devices />;
    }
  };

  const getTipoServicoLabel = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'internet':
        return 'Internet';
      case 'telefonia':
        return 'Telefonia';
      case 'tv':
        return 'TV';
      default:
        return tipo || 'N/A';
    }
  };

  // Agrupar equipamentos por serviço (contrato_servico_id)
  const equipmentsByService = React.useMemo(() => {
    const groups: Record<string, { servico: { id: number; tipo: string; plano?: { nome: string }; usuario_pppoe?: string }; equipments: EquipmentWithCaixa[] }> = {};
    const semServicoKey = '__sem_servico__';

    equipments.forEach((eq) => {
      const servico = eq.contrato_servico;
      const servicoId = eq.contrato_servico_id;

      if (servicoId && servico) {
        const key = servicoId.toString();
        if (!groups[key]) {
          groups[key] = {
            servico: {
              id: servico.id,
              tipo: servico.tipo,
              plano: servico.plano ? { nome: servico.plano.nome } : undefined,
              usuario_pppoe: servico.usuario_pppoe
            },
            equipments: []
          };
        }
        groups[key].equipments.push(eq);
      } else {
        if (!groups[semServicoKey]) {
          groups[semServicoKey] = {
            servico: { id: 0, tipo: 'sem_servico' },
            equipments: []
          };
        }
        groups[semServicoKey].equipments.push(eq);
      }
    });

    // Ordenar: primeiro grupos com serviço (por tipo), depois "Sem serviço"
    const entries = Object.entries(groups);
    const ordenTipo = ['internet', 'telefonia', 'tv', 'sem_servico'];
    return entries.sort((a, b) => {
      const tipoA = a[1].servico.tipo;
      const tipoB = b[1].servico.tipo;
      const idxA = ordenTipo.indexOf(tipoA);
      const idxB = ordenTipo.indexOf(tipoB);
      if (idxA !== idxB) return idxA - idxB;
      return (a[1].servico.plano?.nome || '').localeCompare(b[1].servico.plano?.nome || '');
    });
  }, [equipments]);

  const getRxPowerColor = (rxPower: number) => {
    if (rxPower >= -23 && rxPower <= 2) {
      return 'success'; // Verde - Potência ideal
    } else if (rxPower >= -27 && rxPower < -23) {
      return 'warning'; // Amarelo - Potência aceitável
    } else if (rxPower < -27) {
      return 'error'; // Vermelho - Potência baixa
    } else {
      return 'default'; // Cinza - Fora do range esperado
    }
  };

  const handleEditEquipment = (equipment: EquipmentWithCaixa) => {
    console.log('🚀 ContractEquipments - Abrindo modal de edição para equipamento:', equipment.id);
    setSelectedEquipmentForEdit(equipment);
    setEditEquipmentModalOpen(true);
  };

  const handleCloseEditEquipmentModal = () => {
    setEditEquipmentModalOpen(false);
    setSelectedEquipmentForEdit(null);
  };

  const handleEquipmentEdited = () => {
    // Mostrar toast de sucesso
    Swal.fire({
      title: 'Sucesso!',
      text: 'Equipamento editado com sucesso',
      icon: 'success',
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        container: 'swal-zindex'
      }
    });

    loadEquipments(); // Recarregar a lista de equipamentos
    setEditEquipmentModalOpen(false);
    setSelectedEquipmentForEdit(null);
  };

  const handleRemoveEquipment = async (equipment: EquipmentWithCaixa) => {
    const result = await Swal.fire({
      title: 'Devolver Equipamento',
      html: `
        <div style="text-align: left;">
          <p><strong>Equipamento:</strong> ${equipment.modelo?.titulo || 'N/A'}</p>
          <p><strong>Série:</strong> ${equipment.numero_serie}</p>
          <p><strong>Tipo:</strong> ${equipment.modelo?.tipo || 'N/A'}</p>
          <br>
          <p>Tem certeza que deseja devolver este equipamento?</p>
          ${equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT' ? `
            <p>A ONU/ONT será desvinculada da CAIXA e do Circuito.</p>
          ` : ''}
          ${equipment.modalidade === 'proprio' ? `
            <p style="color: red; font-weight: bold; font-size: 14px;">Ao devolver este equipamento ele será excluido da base de dados do sistema</p>
          ` : ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, devolver!',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'swal-zindex'
      }
    });

    if (result.isConfirmed) {
      try {
        console.log('Devolvendo equipamento do contrato:', equipment.id);

        // Devolver equipamento (remover vinculação com contrato)
        await equipmentService.devolverEquipamento(equipment.id);

        // Mostrar sucesso
        await Swal.fire({
          title: 'Equipamento Devolvido!',
          text: 'O equipamento foi devolvido com sucesso e está disponível para uso.',
          icon: 'success',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            container: 'swal-zindex'
          }
        });

        // Recarregar lista
        loadEquipments();
      } catch (err: any) {
        console.error('Erro ao devolver equipamento:', err);

        // Mostrar erro
        await Swal.fire({
          title: 'Erro!',
          text: 'Erro ao devolver equipamento do contrato',
          icon: 'error',
          confirmButtonText: 'OK',
          customClass: {
            container: 'swal-zindex'
          }
        });

        setError('Erro ao devolver equipamento do contrato');
      }
    }
  };

  const handleAddOnu = () => {
    setAddOnuModalOpen(true);
  };

  const handleCloseAddOnuModal = () => {
    setAddOnuModalOpen(false);
  };

  const handleOnuAdded = () => {
    loadEquipments(); // Recarregar a lista de equipamentos
    setAddOnuModalOpen(false);
  };

  const handleTransferEquipment = (equipment: EquipmentWithCaixa) => {
    setTransferringEquipment(equipment);
    setTransferModalOpen(true);
  };

  const handleConfirmTransfer = async (newContractId: number, newServicoId: number) => {
    if (!transferringEquipment) return;

    try {
      setTransferring(true);

      const response = await equipmentService.transferirEquipmento(transferringEquipment.id, newContractId, newServicoId);
      console.log('🚀 ContractEquipments - Resposta da transferência:', response);
      if (response.success) {
        await Swal.fire({
          title: 'Sucesso!',
          text: 'Equipamento transferido com sucesso!',
          icon: 'success',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            container: 'swal-zindex'
          }
        });

        setTransferModalOpen(false);
        setTransferringEquipment(null);
        loadEquipments();
      } else {
        await Swal.fire({
          title: 'Erro!',
          text: response.message || 'Erro ao transferir equipamento',
          icon: 'error',
          customClass: {
            container: 'swal-zindex'
          }
        });
      }
    } catch (err: any) {
      console.error('Erro ao transferir equipamento:', err);
      await Swal.fire({
        title: 'Erro!',
        text: 'Erro ao transferir equipamento: ' + err.message,
        icon: 'error',
        customClass: {
          container: 'swal-zindex'
        }
      });
    } finally {
      setTransferring(false);
    }
  };

  const handleCloseTransferModal = () => {
    if (!transferring) {
      setTransferModalOpen(false);
      setTransferringEquipment(null);
    }
  };

  const handleViewCaixaDetails = (caixaId: number) => {
    navigate(`/ftth/caixas/${caixaId}`);
  };

  const handleVincularCircuito = (equipmentId: number) => {
    const equipment = equipments.find(eq => eq.id === equipmentId);
    if (!equipment) return;

    if (!equipment.caixa_id) {
      Swal.fire({
        title: 'Atenção',
        text: 'O equipamento precisa estar vinculado a uma caixa para selecionar um circuito.',
        icon: 'warning',
        customClass: {
          container: 'swal-zindex'
        }
      });
      return;
    }

    setSelectedEquipmentForCircuito(equipment);
    setVincularCircuitoModalOpen(true);
  };

  const handleCloseVincularCircuitoModal = () => {
    setVincularCircuitoModalOpen(false);
    setSelectedEquipmentForCircuito(null);
  };

  const handleVincularCircuitoSuccess = () => {
    Swal.fire({
      title: 'Sucesso!',
      text: 'Circuito vinculado com sucesso!',
      icon: 'success',
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        container: 'swal-zindex'
      }
    });
    setVincularCircuitoModalOpen(false);
    setSelectedEquipmentForCircuito(null);
    loadEquipments();
  };

  const handleTrocarCto = (equipment: EquipmentWithCaixa) => {
    if (!equipment.caixa_id) {
      Swal.fire({
        title: 'Atenção',
        text: 'O equipamento precisa estar vinculado a uma caixa para trocar de CTO.',
        icon: 'warning',
        customClass: { container: 'swal-zindex' }
      });
      return;
    }
    setSelectedEquipmentForTrocarCto(equipment);
    setTrocarCtoModalOpen(true);
  };

  const handleCloseTrocarCtoModal = () => {
    setTrocarCtoModalOpen(false);
    setSelectedEquipmentForTrocarCto(null);
  };

  const handleTrocarCtoSuccess = () => {
    Swal.fire({
      title: 'Sucesso!',
      text: 'CTO trocada com sucesso!',
      icon: 'success',
      timer: 3000,
      timerProgressBar: true,
      customClass: { container: 'swal-zindex' }
    });
    setTrocarCtoModalOpen(false);
    setSelectedEquipmentForTrocarCto(null);
    loadEquipments();
  };

  const handleTrocarEquipamento = (equipment: EquipmentWithCaixa) => {
    setSelectedEquipmentForTrocarEquipamento(equipment);
    setTrocarEquipamentoModalOpen(true);
  };

  const handleCloseTrocarEquipamentoModal = () => {
    setTrocarEquipamentoModalOpen(false);
    setSelectedEquipmentForTrocarEquipamento(null);
  };

  const handleTrocarEquipamentoSuccess = () => {
    setTrocarEquipamentoModalOpen(false);
    setSelectedEquipmentForTrocarEquipamento(null);
    loadEquipments();
  };

  const handleDesvincularCircuito = async (equipmentId: number) => {
    const equipment = equipments.find(eq => eq.id === equipmentId);
    if (!equipment) return;

    Swal.fire({
      title: 'Desvincular Circuito',
      text: `Deseja realmente desvincular o circuito do equipamento ${equipment.modelo?.titulo || ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, desvincular',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'swal-zindex'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await equipmentService.updateEquipment(equipmentId, {
            circuito_id: null
          });
          Swal.fire({
            title: 'Sucesso!',
            text: 'Circuito desvinculado com sucesso!',
            icon: 'success',
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              container: 'swal-zindex'
            }
          });
          loadEquipments();
        } catch (error: any) {
          console.error('Erro ao desvincular circuito:', error);
          Swal.fire({
            title: 'Erro!',
            text: error.response?.data?.message || 'Ocorreu um erro ao desvincular o circuito.',
            icon: 'error',
            customClass: {
              container: 'swal-zindex'
            }
          });
        }
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Carregando equipamentos...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: '1rem' }}>
          Equipamentos do Contrato
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={handleAddOnu}
            disabled={contractStatus === 'cancelado'}
            sx={{ fontSize: '0.8rem', py: 0.5 }}
          >
            Adicionar ONT/ONU
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={() => setAddEquipamentoModalOpen(true)}
            disabled={contractStatus === 'cancelado'}
            sx={{ fontSize: '0.8rem', py: 0.5 }}
          >
            Adicionar Equipamento
          </Button>
        </Box>
      </Box>

      {/* Lista de Equipamentos agrupados por serviço */}
      {equipments.length === 0 ? (
        <Card variant="outlined">
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum equipamento vinculado. Use os botões acima para adicionar.
            </Typography>
          </Box>
        </Card>
      ) : (
        equipmentsByService.map(([groupKey, { servico, equipments: groupEquipments }]) => (
          <Card key={groupKey} variant="outlined" sx={{ mb: 1 }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <Box component="span" sx={{ display: 'flex', '& svg': { fontSize: 18 } }}>
                {getTipoServicoIcon(servico.tipo)}
              </Box>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                {servico.tipo === 'sem_servico'
                  ? 'Sem serviço vinculado'
                  : `${servico.plano?.nome || 'Sem plano'} - ${getTipoServicoLabel(servico.tipo)}`}
              </Typography>
              {servico.usuario_pppoe && (
                <Tooltip title="Usuário PPPoE" arrow>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 1,
                      color: 'text.secondary',
                    }}
                  >
                    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {servico.usuario_pppoe}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Equipamento</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Provisionamento</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Modalidade</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>CTO</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Sinal PON</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupEquipments.map((equipment) => (
                    <TableRow key={equipment.id} hover>
                      <TableCell sx={{ py: 0.35, px: 1 }}>
                        <Chip
                          icon={getTypeIcon(equipment.modelo?.tipo || '')}
                          label={getTypeLabel(equipment.modelo?.tipo || '')}
                          variant="outlined"
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { fontSize: 14 } }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Avatar
                            src={equipment.modelo?.imagem ? equipmentService.getModelImageUrl(equipment.modelo.imagem) : undefined}
                            sx={{ width: 24, height: 24, cursor: equipment.modelo?.imagem ? 'pointer' : 'default', bgcolor: 'transparent', color: 'text.secondary', '& svg': { fontSize: 16 } }}
                            onClick={() => {
                              if (equipment.modelo?.imagem) {
                                setSelectedImageSrc(equipmentService.getModelImageUrl(equipment.modelo.imagem));
                                setImageModalOpen(true);
                              }
                            }}
                          >
                            {getTypeIcon(equipment.modelo?.tipo || '')}
                          </Avatar>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem', display: 'block' }}>
                              {equipment.modelo?.titulo || 'N/A'}
                            </Typography>
                            {(equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT') && equipment.gpon_sn && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {equipment.gpon_sn}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                              {equipment.numero_serie}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.35, px: 1 }}>
                        {(equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT') && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Chip
                              icon={getStatusIcon(equipment.provision_status || 'pending')}
                              label={getStatusLabel(equipment.provision_status || 'pending')}
                              color={getStatusColor(equipment.provision_status || 'pending') as any}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                            />
                            {equipment.onu_id !== undefined && equipment.onu_id !== null && (
                              <Chip label={`ID: ${equipment.onu_id}`} color="primary" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>)}
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 1 }}>
                        <Chip
                          label={getModalidadeLabel(equipment.modalidade)}
                          color={getModalidadeColor(equipment.modalidade) as any}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 1 }}>
                        {(equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT') && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            <Chip
                              onClick={() => handleViewCaixaDetails(equipment.caixa?.id || 0)}
                              label={equipment.caixa?.name + ' / ' + 'Porta: ' + equipment.caixa_port_number || 'N/A'}
                              color="primary"
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {equipment.circuito_id && (
                              <Chip
                                label={equipment.circuito?.identificacao ? `Circuito ${equipment.circuito.identificacao}` : `Circuito ID: ${equipment.circuito_id}`}
                                color="secondary"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                                onClick={() => handleOpenCircuitoView(equipment.circuito_id!)}
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 1 }}>
                        {(equipment.modelo?.tipo === 'ONU' || equipment.modelo?.tipo === 'ONT') && (equipment.txPower !== null && equipment.txPower !== undefined ? (
                          <Chip
                            label={`${equipment.rxPower} dBm`}
                            color={getRxPowerColor(equipment.rxPower || 0) as any}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>N/A</Typography>
                        ))}
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 0.5 }}>
                        <IconButton onClick={(e) => handleContextMenu(e, equipment)} size="small" sx={{ p: 0.25 }}>
                          <MoreVert sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ))
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Pagination
            size="small"
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Modais */}
      <AddOnuModal
        open={addOnuModalOpen}
        onClose={handleCloseAddOnuModal}
        onOnuAdded={handleOnuAdded}
        contractId={contractId}
      />

      {/* Novo Modal para Equipamentos (Roteador/Switch) */}
      <AddEquipamentoModal
        open={addEquipamentoModalOpen}
        onClose={() => setAddEquipamentoModalOpen(false)}
        onEquipmentAdded={loadEquipments}
        contractId={contractId}
      />

      {/* Modal de Edição de Equipamento */}
      <EquipmentForm
        open={editEquipmentModalOpen}
        onClose={handleCloseEditEquipmentModal}
        onSuccess={handleEquipmentEdited}
        equipment={selectedEquipmentForEdit}
      />

      {selectedEquipmentForProvision && (
        <ProvisionarModal
          open={provisionarModalOpen}
          onClose={handleCloseProvisionarModal}
          equipment={{
            id: selectedEquipmentForProvision.id,
            name: selectedEquipmentForProvision.modelo?.titulo || 'N/A',
            gpon_sn: selectedEquipmentForProvision.gpon_sn || 'N/A',
            mac_address: selectedEquipmentForProvision.mac_address || 'N/A',
            model: selectedEquipmentForProvision.modelo?.titulo || 'N/A',
            provision_name: selectedEquipmentForProvision.observacoes || undefined
          }}
          cto={{
            id: selectedEquipmentForProvision.caixa?.id || 0,
            name: selectedEquipmentForProvision.caixa?.name || 'N/A',
            address: 'N/A',
            olt_id: selectedEquipmentForProvision.caixa?.olt_id || 0
          }}
          onProvisionSuccess={handleProvisionSuccess}
        />
      )}

      {/* Menu de Contexto */}
      <EquipmentContextMenu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        equipment={selectedEquipment}
        onEdit={handleEditEquipment}
        onDevolver={handleRemoveEquipment}
        onProvisionar={handleProvisionar}
        onDesprovisionar={handleDesprovisionar}
        onChecarSinal={handleChecarSinal}
        onReiniciar={handleReiniciar}
        onDiagnosticarONU={handleDiagnosticarONU}
        onTransferir={handleTransferEquipment}
        onVincularCircuito={handleVincularCircuito}
        onDesvincularCircuito={handleDesvincularCircuito}
        onTrocarCto={handleTrocarCto}
        onTrocarEquipamento={handleTrocarEquipamento}
      />

      {/* Modal de Transferência */}
      <TransferEquipamentoModal
        open={transferModalOpen}
        onClose={handleCloseTransferModal}
        onConfirm={handleConfirmTransfer}
        equipamento={transferringEquipment ? {
          id: transferringEquipment.id,
          modelo: transferringEquipment.modelo,
          numero_serie: transferringEquipment.numero_serie,
          mac_address: transferringEquipment.mac_address || '',
          gpon_sn: transferringEquipment.gpon_sn
        } : null}
        loading={transferring}
      />

      {/* Modal de Vincular Circuito */}
      <VincularCircuitoModal
        open={vincularCircuitoModalOpen}
        onClose={handleCloseVincularCircuitoModal}
        onSuccess={handleVincularCircuitoSuccess}
        equipment={selectedEquipmentForCircuito}
      />

      {/* Modal Trocar de CTO */}
      <TrocarCtoModal
        open={trocarCtoModalOpen}
        onClose={handleCloseTrocarCtoModal}
        onSuccess={handleTrocarCtoSuccess}
        equipment={selectedEquipmentForTrocarCto}
        contractId={contractId}
      />

      {/* Modal Trocar Equipamento */}
      <TrocarEquipamentoModal
        open={trocarEquipamentoModalOpen}
        onClose={handleCloseTrocarEquipamentoModal}
        onSuccess={handleTrocarEquipamentoSuccess}
        equipment={selectedEquipmentForTrocarEquipamento}
        contractId={contractId}
      />

      {/* Modal Visualizar Circuito */}
      {selectedCircuitoId && (
        <CircuitoViewModal
          open={circuitoViewModalOpen}
          onClose={handleCloseCircuitoView}
          circuitoId={selectedCircuitoId}
        />
      )}

      {/* Modal de Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Imagem do Modelo
          <IconButton onClick={() => setImageModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
          {selectedImageSrc && (
            <img src={selectedImageSrc} alt="Modelo" style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px' }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContractEquipments; 